import {Trans} from '@lingui/macro';
import _, {first, flatMap} from 'lodash';
import React, {useState} from 'react';
import styled from 'styled-components';

import {parseQueryString} from '../../../../libs/shared/helpers/src/browser/urlHelpers';
import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {flexCenter} from '../../../../libs/shared/ui/src/styles/mixins';
import {useLocalize} from '../../../../srcCommon/internationalization/internationalizationContext';
import {AccountsSetupGuideContainer} from '../../../components/accounts/accountsSetupGuide/accountsSetupGuideContainer';
import {useMeasureElement} from '../../../helpers/hooks/domHookHelpers';
import {buildSettingsPath} from '../../../models/url/settingsUrlHelpers';
import {SettingsResourcePagesEnum, SettingsRulesUrl} from '../../../models/url/settingsUrlModel';
import {Localizer} from '../../../store/stateless/internationalizationSelectors';
import {
  StyledSettingsContentWrap,
  StyledSettingsMain,
  StyledSettingsWrapper,
} from '../../common/settingsWrapper';
import {ruleLibrarySearchKey} from '../../menu/settingsHelpers';
import {RuleHeader} from '../ruleHeader';
import {RuleIntegration} from '../ruleIntegrationModel';
import {RuleRecipe, useRuleLibraryRecipes} from './recipes';
import {RULE_LIBRARY_GAP_SIZE, RuleLibraryCategory} from './ruleLibraryCategory';
import {DisplayableRuleRecipe, RuleLibraryRecipeCard} from './ruleLibraryRecipeCard';
import {
  makeRuleLibrarySearchFilter,
  RuleLibrarySearch,
  RuleLibrarySearchFilter,
  RuleLibrarySearchState,
} from './ruleLibrarySearch';
import {ruleLibrarySections} from './ruleLibrarySections';

/*
 * Props.
 */

interface RuleLibraryProps {
  rulesUrl: SettingsRulesUrl;
  integrations: RuleIntegration;
}

/*
 * Styles.
 */

const StyledSectionsWrapperDiv = styled.div`
  width: 100%;
`;

const StyledSearchResults = styled.div`
  ${flexCenter};
  flex-wrap: wrap;

  & > * {
    margin-right: ${RULE_LIBRARY_GAP_SIZE}px;
    margin-bottom: ${RULE_LIBRARY_GAP_SIZE}px;
  }
`;

const StyledSetupGuideDiv = styled.div`
  min-width: 248px;
`;

/*
 * Component.
 */

export const RuleLibrary = ({rulesUrl, integrations}: RuleLibraryProps) => {
  const recipes = useRuleLibraryRecipes(integrations);
  const localize = useLocalize();
  const [searchState, setSearchState] = useState<RuleLibrarySearchState>({});
  const [containerRef, {width}] = useMeasureElement();
  const [isAccountsSetupGuideVisible, setIsAccountsSetupGuideVisible] = useState(false);

  const searchFilter = makeRuleLibrarySearchFilter(searchState);

  useInitialEffect(() => {
    const {[ruleLibrarySearchKey]: ruleLibrarySearch} = parseQueryString(window.location.search);
    if (!ruleLibrarySearch) {
      return;
    }

    if (ruleLibrarySearch === 'account') {
      setIsAccountsSetupGuideVisible(true);
    }

    setSearchState({searchQuery: ruleLibrarySearch});
  });

  return (
    <StyledSettingsWrapper>
      <RuleHeader rulesUrl={rulesUrl}>
        <Trans id="settings-rules-header-title">Rules</Trans>
      </RuleHeader>
      <StyledSettingsMain>
        <StyledSettingsContentWrap>
          <StyledSectionsWrapperDiv ref={containerRef}>
            <RuleLibrarySearch value={searchState} onChange={setSearchState} />
            {searchFilter
              ? renderSearchResults(recipes, searchFilter, localize, rulesUrl)
              : renderSections(recipes, width, localize, rulesUrl)}
          </StyledSectionsWrapperDiv>
          {isAccountsSetupGuideVisible && (
            <StyledSetupGuideDiv>
              <AccountsSetupGuideContainer shouldHideCompletedGuide />
            </StyledSetupGuideDiv>
          )}
        </StyledSettingsContentWrap>
      </StyledSettingsMain>
    </StyledSettingsWrapper>
  );
};

/*
 * Helpers.
 */

function setRecipeUrl(
  recipe: Omit<DisplayableRuleRecipe, 'url'>,
  rulesUrl: SettingsRulesUrl,
): DisplayableRuleRecipe {
  return {
    ...recipe,
    url: buildSettingsPath({
      ...rulesUrl,
      resourcePage: SettingsResourcePagesEnum.RECIPES,
      recipe: recipe.id,
    }),
  };
}

function renderSections(
  recipes: ReadonlyArray<RuleRecipe>,
  sectionWidth: number,
  localize: Localizer,
  rulesUrl: SettingsRulesUrl,
) {
  return ruleLibrarySections.map((section) => {
    const displayableRecipes = section.content(recipes, localize);

    if (!displayableRecipes.length) {
      return null;
    }

    return (
      <RuleLibraryCategory
        key={section.id}
        title={section.title}
        sectionWidth={sectionWidth}
        recipes={displayableRecipes.map((recipe) => setRecipeUrl(recipe, rulesUrl))}
        imageUrl={section.imageUrl}
        backgroundColor={section.backgroundColor}
        learnMore={section.learnMore}
      />
    );
  });
}

function renderSearchResults(
  recipes: ReadonlyArray<RuleRecipe>,
  searchFilter: RuleLibrarySearchFilter,
  localize: Localizer,
  rulesUrl: SettingsRulesUrl,
) {
  const allRecipes = flatMap(ruleLibrarySections, (section) =>
    section.content(recipes, localize).map((recipe) => setRecipeUrl(recipe, rulesUrl)),
  );

  const uniqueRecipes = _(allRecipes)
    .compact()
    .groupBy((recipe) => recipe.id)
    .map(findPreferredRecipe)
    .compact()
    .value();

  return (
    <StyledSearchResults>
      {searchFilter(uniqueRecipes).map((recipe) => (
        <RuleLibraryRecipeCard key={recipe.id} recipe={recipe} />
      ))}
    </StyledSearchResults>
  );
}

/**
 * For a list of duplicate recipes, attempt to return one with an image url
 * and otherwise return the first available recipe.
 */
function findPreferredRecipe(
  recipes: ReadonlyArray<DisplayableRuleRecipe>,
): DisplayableRuleRecipe | undefined {
  const recipeWithImage = recipes.find((recipe) => Boolean(recipe.imageUrl));
  return recipeWithImage ?? first(recipes);
}
